import React, { createContext, useMemo } from 'react';
import { useCurrentUserContext, useIsSuperAdmin } from '../api/swrcurrentusercontext';
import { matchPath } from 'react-router-dom';

// UserProfile class with utility methods
class UserProfile {
  constructor(userContext, isSuperAdmin) {
    this.currentUser = userContext.user;
    this.currentCustomer = userContext.customer;
    // Derived: if current customer is an Org, currentOrg = customer; else null
    this.currentOrg = userContext.customer?.orgName ? userContext.customer : null;
    this.currentCase = userContext.case;
    this.orgGroups = userContext?.orgGroups;
    this.caseGroups = userContext?.caseGroups;
    this.isSuperAdmin = isSuperAdmin;
    this.accessibleRoutes = userContext?.accessibleRoutes || [];
    // Routes that exist in the app but this user's groups don't grant. The
    // router registers them against AccessDeniedPage so a permission miss
    // reads as such instead of falling through to the catch-all 404.
    // Path-only by design — the backend withholds component and group list.
    this.restrictedRoutes = userContext?.restrictedRoutes || [];
  }

  // ── Customer awareness ──

  /** Whether the current user has the :Customer label */
  get isCustomer() {
    // Check if the user themselves is a Customer (has customerID on their user node,
    // or if they have billing properties)
    return !!(this.currentUser?.billingStrategy || this.currentUser?.qboCustomerId);
  }

  /** Whether the current customer context is set */
  get hasCustomerContext() {
    return !!this.currentCustomer;
  }

  /** The type of the current customer: 'organization', 'user', or null */
  get customerType() {
    if (!this.currentCustomer) return null;
    return this.currentCustomer.customerType || (this.currentCustomer.orgName ? 'organization' : 'user');
  }

  // ── Group checks ──

  hasCaseGroup(groupName) {
    if (!this.caseGroups) return false;
    return this.caseGroups.some(group => group === groupName);
  }

  hasOrgGroup(groupName) {
    if (!this.orgGroups) return false;
    return this.orgGroups.some(group => group === groupName);
  }

  isOrgAdmin() {
    return this.hasOrgGroup('Org Admins') || this.isSuperAdmin;
  }

  /** Whether the user is the admin of the current Customer context.
   *  For Customer:Organization, this means Org Admin.
   *  For Customer:User, check if the user IS the customer. */
  isCustomerAdmin() {
    if (this.isSuperAdmin) return true;
    if (!this.currentCustomer) return false;
    // If the customer is a User, only that user is admin
    if (this.customerType === 'user') {
        return this.currentUser?.userID === this.currentCustomer.customerID;
    }
    // Otherwise check org admin
    return this.isOrgAdmin();
}

  isCaseAdmin() {
    return this.hasCaseGroup('Admins') || this.isOrgAdmin();
  }

  isCaseEditor() {
    return this.hasCaseGroup('Editors') || this.isCaseAdmin();
  }

  isCaseContributor() {
    return this.hasCaseGroup('Contributors') || this.isCaseEditor();
  }

  isCaseViewer() {
    return this.hasCaseGroup('Viewers') || this.isCaseContributor();
  }

  // ── Route access ──

  canAccess(path) {
    if (!this.accessibleRoutes || !path) return false;
    // matchPath compares pathnames, so a target carrying a query or hash
    // (the dashboard's virtual cards use e.g.
    // /report_sankey_analysis/CATEGORY?stat=flagged) would never match a
    // static route pattern and would look inaccessible. Strip both first.
    const pathname = path.split(/[?#]/)[0];
    return this.accessibleRoutes.some(route => {
      return matchPath(route.path, pathname) !== null;
    });
  }
}

const UserProfileContext = createContext({
  isLoading: true,
  userProfile: null
});

export const UserProfileProvider = ({ children }) => {
  const { data: userContext, isLoading: isUserContextLoading } = useCurrentUserContext();
  const { data: isSuperAdmin, isLoading: isLoadingSuperAdmin } = useIsSuperAdmin();

  const isLoading = isUserContextLoading || isLoadingSuperAdmin;

  const userProfile = useMemo(() => {
    return userContext ? new UserProfile(userContext, isSuperAdmin) : null;
  }, [userContext, isSuperAdmin]);

  return (
    <UserProfileContext.Provider value={{ userProfile, isLoading }}>
      {children}
    </UserProfileContext.Provider>
  );
};

export default UserProfileContext;